import {
  isPublishableStatus,
  isRejectableStatus,
} from "@app/shared/models/enrichment-list.model";
import { PublishScheduleAction } from "@app/shared/models/publish-schedule.model";
import { Permission, Permissions } from "@core/models/permissions.generated";
import { EnrichmentPriceRow, PricesByOffering } from "./enrichment-list.types";
import { OpenScheduleIndex, openScheduleOf } from "./open-schedules";

export type LifecycleAction =
  "publish" | "reject" | "schedulePublish" | "scheduleUnpublish";

export interface LifecycleActionSpec {
  /** Row-menu action id. */
  menuId: string;
  icon: string;
  label: string;
  /** The status a price needs for the action. */
  requires: string;
  /** Static row-menu tooltip: it has to read sensibly enabled and greyed. */
  tooltip: string;
  /** Opening line of the confirm drawer. */
  intro: string;
  /** Past tense, for the results. */
  done: string;
  /** The BFF gates scheduling a take-down on reject, not publish. */
  permission: Permission;
}

export const LIFECYCLE_ACTIONS: Record<LifecycleAction, LifecycleActionSpec> = {
  publish: {
    menuId: "publish",
    icon: "publish",
    label: "Publish",
    requires: "Designed",
    tooltip:
      "Publish this price on the selected channel now. Available while the price is Designed and enriched on the channel.",
    intro: "These prices go on sale now.",
    done: "published",
    permission: Permissions.EnrichmentPublish,
  },
  reject: {
    menuId: "reject",
    icon: "block",
    label: "Reject",
    requires: "Active or Launched",
    tooltip:
      "Stop selling this price on the selected channel. It becomes Rejected and keeps its enrichment. Available while the price is Active or Launched on the channel.",
    intro:
      "These prices come off sale now and become Rejected. Their enrichment is kept, so they can be published again from the editor.",
    done: "rejected",
    permission: Permissions.EnrichmentReject,
  },
  schedulePublish: {
    menuId: "schedule-publish",
    icon: "schedule",
    label: "Schedule publish",
    requires: "Designed",
    tooltip:
      "Publish this price at a time you choose. Available while the price is Designed and enriched on the channel.",
    intro:
      "These prices go on sale at the time you pick, if they are still Designed then.",
    done: "scheduled to publish",
    permission: Permissions.EnrichmentPublish,
  },
  scheduleUnpublish: {
    menuId: "schedule-unpublish",
    icon: "event_busy",
    label: "Schedule stop selling",
    requires: "Active or Launched",
    tooltip:
      "Take this price off sale at a time you choose. It returns to Designed and keeps its enrichment. Available while the price is Active or Launched on the channel.",
    intro:
      "These prices come off sale at the time you pick and return to Designed, keeping their enrichment.",
    done: "scheduled to stop selling",
    permission: Permissions.EnrichmentReject,
  },
};

/** The actions the list offers, in menu order. Publishing now stays in the
 *  editor, which refuses a price with readiness errors. Neither this list
 *  nor the BFF checks readiness. */
export const LIFECYCLE_ACTION_ORDER: readonly LifecycleAction[] = [
  "reject",
  "schedulePublish",
  "scheduleUnpublish",
];

export type TransitionSkipReason =
  "status" | "notOnChannel" | "alreadyScheduled" | "duplicate";

export interface TransitionSkip {
  priceId: string;
  /** The skipped row's package. */
  offeringId: string;
  reason: TransitionSkipReason;
}

export interface TransitionPlan {
  requests: PricesByOffering;
  skipped: readonly TransitionSkip[];
}

const STATUS_ALLOWS: Record<
  LifecycleAction,
  (status?: string | null) => boolean
> = {
  publish: isPublishableStatus,
  schedulePublish: isPublishableStatus,
  reject: isRejectableStatus,
  scheduleUnpublish: isRejectableStatus,
};

const SCHEDULE_DIRECTION: Record<
  LifecycleAction,
  PublishScheduleAction | null
> = {
  publish: null,
  reject: null,
  schedulePublish: "publish",
  scheduleUnpublish: "unpublish",
};

export function scheduleDirection(
  action: LifecycleAction,
): PublishScheduleAction | null {
  return SCHEDULE_DIRECTION[action];
}

/** Why `row` cannot carry `action`, or null when it can. */
export function ineligibility(
  row: EnrichmentPriceRow,
  action: LifecycleAction,
  openSchedules: OpenScheduleIndex,
): Exclude<TransitionSkipReason, "duplicate"> | null {
  if (!row.onChannel) return "notOnChannel";
  if (!STATUS_ALLOWS[action](row.status)) return "status";
  const direction = SCHEDULE_DIRECTION[action];
  if (direction && openScheduleOf(openSchedules, row.priceId, direction)) {
    return "alreadyScheduled";
  }
  return null;
}

/**
 * The only place a selection of price rows becomes package-addressed
 * lifecycle requests.
 *
 * Status and schedules are keyed by (price, channel), not (package, price),
 * so each price is sent once: through its first eligible row, preferring a
 * package that also carries another eligible selected price, so fewer
 * requests go out. The price's other rows are skipped as `duplicate`. A price
 * with no eligible row is skipped once, with the reason of an on-channel row
 * when it has one. No package ever gets an empty list.
 */
export function planTransitions(
  selection: readonly EnrichmentPriceRow[],
  action: LifecycleAction,
  openSchedules: OpenScheduleIndex,
): TransitionPlan {
  const rowsByPrice = new Map<string, EnrichmentPriceRow[]>();
  for (const row of selection) {
    const rows = rowsByPrice.get(row.priceId);
    if (rows) rows.push(row);
    else rowsByPrice.set(row.priceId, [row]);
  }

  const skipped: TransitionSkip[] = [];
  const eligibleByPrice = new Map<string, EnrichmentPriceRow[]>();
  for (const [priceId, rows] of rowsByPrice) {
    const reasons = rows.map((row) =>
      ineligibility(row, action, openSchedules),
    );
    const eligible = rows.filter((_, i) => reasons[i] === null);
    if (eligible.length > 0) {
      eligibleByPrice.set(priceId, eligible);
      continue;
    }
    const reason =
      reasons.find((r) => r !== null && r !== "notOnChannel") ?? "notOnChannel";
    skipped.push({ priceId, offeringId: rows[0].offeringId, reason });
  }

  const eligiblePricesPerOffering = new Map<string, number>();
  for (const rows of eligibleByPrice.values()) {
    for (const offeringId of new Set(rows.map((row) => row.offeringId))) {
      eligiblePricesPerOffering.set(
        offeringId,
        (eligiblePricesPerOffering.get(offeringId) ?? 0) + 1,
      );
    }
  }

  const requests = new Map<string, [string, ...string[]]>();
  for (const [priceId, eligible] of eligibleByPrice) {
    const carrier =
      eligible.find(
        (row) => (eligiblePricesPerOffering.get(row.offeringId) ?? 0) > 1,
      ) ?? eligible[0];
    const ids = requests.get(carrier.offeringId);
    if (ids) ids.push(priceId);
    else requests.set(carrier.offeringId, [priceId]);
    for (const row of rowsByPrice.get(priceId) ?? []) {
      if (row !== carrier) {
        skipped.push({
          priceId,
          offeringId: row.offeringId,
          reason: "duplicate",
        });
      }
    }
  }
  return { requests, skipped };
}

export function whyNoneEligible(
  plan: TransitionPlan,
  action: LifecycleAction,
  channelLabel: string,
): string {
  const spec = LIFECYCLE_ACTIONS[action];
  const reasons = new Set(plan.skipped.map((skip) => skip.reason));
  const [only] = reasons.size === 1 ? reasons : [];
  switch (only) {
    case "alreadyScheduled":
      return `Every selected price already has a ${SCHEDULE_DIRECTION[action] === "unpublish" ? "take-down" : "publish"} scheduled`;
    case "notOnChannel":
      return `None of the selected prices is enriched on ${channelLabel} yet`;
    case "status":
      return `None of the selected prices is ${spec.requires}`;
    default:
      return `None of the selected prices can be ${spec.done}: a price must be ${spec.requires} and enriched on ${channelLabel}`;
  }
}

/** Distinct prices a plan sends. */
export function plannedPriceCount(plan: TransitionPlan): number {
  let n = 0;
  for (const ids of plan.requests.values()) n += ids.length;
  return n;
}
